/**
 * Author: Claude Code
 * Generation: Landing page with Suno-style typewriter effect and i18n support
 * Version: v3.2.54
 * Modified: 2025-10-09
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useAuthStore } from '@store/AuthStore';
import { usePlatformStats } from '@store/StatsStore';

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, openAuthModal } = useAuthStore();
  const stats = usePlatformStats();

  // Typewriter effect states - v3.2.54: i18n support
  const heroTexts = [
    t('landing.heroText1'),
    t('landing.heroText2'),
    t('landing.heroText3'),
    t('landing.heroText4')
  ];

  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCursor, setShowCursor] = useState(true);

  // Typewriter effect logic
  useEffect(() => {
    const currentFullText = heroTexts[currentTextIndex];

    const typeSpeed = isDeleting ? 50 : 100;
    const pauseTime = isDeleting ? 500 : 2000;

    if (!isDeleting && displayedText === currentFullText) {
      // Finished typing, pause then start deleting
      const timer = setTimeout(() => setIsDeleting(true), pauseTime);
      return () => clearTimeout(timer);
    }

    if (isDeleting && displayedText === '') {
      // Finished deleting, move to next text
      setIsDeleting(false);
      setCurrentTextIndex((prev) => (prev + 1) % heroTexts.length);
      return;
    }

    const timer = setTimeout(() => {
      if (isDeleting) {
        setDisplayedText(currentFullText.substring(0, displayedText.length - 1));
      } else {
        setDisplayedText(currentFullText.substring(0, displayedText.length + 1));
      }
    }, typeSpeed);

    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentTextIndex, heroTexts]);

  // Cursor blinking effect
  useEffect(() => {
    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);

    return () => clearInterval(cursorTimer);
  }, []);

  const handleStartGame = () => {
    if (!isAuthenticated) {
      openAuthModal('signin', '/rules'); // v3.2.57: Redirect to rules after auth
    } else {
      navigate('/rules');
    }
  };

  return (
    <div className="main-container">
      <div className="page active">
        <div className="hero-content">
          {/* Badge with animation - v3.2.54: i18n support */}
          <motion.div
            className="hero-badge"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span>✨</span>
            <span>{t('landing.badge')}</span>
          </motion.div>

          {/* Typewriter effect title */}
          <div className="hero-title-container">
            <h1 className="hero-title">
              {displayedText}
              <span
                className="hero-cursor"
                style={{ opacity: showCursor ? 1 : 0 }}
              />
            </h1>
          </div>

          {/* Subtitle with fade-in - v3.2.54: i18n support */}
          <motion.p
            className="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {t('landing.subtitle')}
          </motion.p>

          {/* v3.2.53: Action buttons with unified font */}
          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <motion.button
              className="btn btn-primary btn-lg hero-btn"
              onClick={handleStartGame}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <Sparkles className="btn-icon" />
              {t('buttons.startPlaying')}
            </motion.button>
            <motion.button
              className="btn btn-ghost btn-lg hero-btn"
              onClick={() => navigate('/rules')}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              {t('buttons.watchAsGuest')}
            </motion.button>
          </motion.div>

          {/* v3.2.54: Real-time statistics with i18n support */}
          <motion.div
            className="hero-stats"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            {[
              { value: `${stats.totalAgents}+`, label: t('stats.aiAgents'), dynamic: true },
              { value: `${stats.totalBattles}+`, label: t('stats.battlesFought'), dynamic: true },
              { value: stats.onlineViewers, label: t('stats.liveStreaming'), dynamic: false }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                className="hero-stat-item"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.9 + index * 0.1 }}
                whileHover={{ scale: 1.05, y: -2 }}
                title={stat.dynamic ? "Real-time data" : undefined}
              >
                <div className="hero-stat-value hero-font-light">{stat.value}</div>
                <div className="hero-stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
